import React, { useState, useEffect } from 'react'
import { T, Spinner, probColor, tdColor, playColor } from '../ui'
import { getNFLScorecard, getNFLMarketPulse, getNFLPickemBoard, getNFLReferral, getNFLSlate,
  getNFLLocks, getNFLFades, getNFLScorers, getNFLTdRoles, NFL_MARKETS } from '../api'
import { grade as tdGrade } from './TdRolesTab'
import { track } from '../track'
import { playText, roleText, teamNick } from '../plain'

// My Board — a user-built dashboard. Anything in the app can go on it: live cards (a self-
// fetching summary of a board) and shortcut tiles (one tap to any screen or tool). Pick, reorder,
// or start from a template; the layout saves on this device.
const KEY = 'brkr_nfl_myboard'
// Live cards — each reads its own data, so the board works no matter which are on.
const SECTIONS = {
  record:   { icon: '📈', title: 'Record & CLV', dest: 'record' },
  slate:    { icon: '🏈', title: "This week's slate", dest: 'games' },
  locks:    { icon: '🔒', title: 'Most likely to cash', dest: 'home:locks' },
  fades:    { icon: '⚠️', title: 'Overs to fade (unders)', dest: 'home:fades' },
  scorers:  { icon: '🏈', title: 'Most likely to score a TD', dest: 'scorers' },
  tdroles:  { icon: '🎯', title: 'Best TD matchups', dest: 'tdroles' },
  pulse:    { icon: '📊', title: 'Market Pulse', dest: 'more:pulse' },
  pickem:   { icon: '🎯', title: "Pick'em +EV", dest: 'more:pickem' },
  refer:    { icon: '🎁', title: 'Refer & Earn', dest: 'refer' },
}
// Shortcut tiles — every screen and tool in the app, grouped the way the app is laid out.
// Old ids (go_paydirt, go_board…) are kept so saved boards keep working.
const SHORTCUTS = {
  go_home:       { g: 'Main', icon: '🏠', title: 'This Week', dest: 'home' },
  go_studio:     { g: 'Main', icon: '🧪', title: 'Play Lab (generate a play)', dest: 'playlab' },
  go_tds:        { g: 'Main', icon: '🏈', title: 'Touchdowns', dest: 'touchdowns' },
  go_games:      { g: 'Main', icon: '🎮', title: 'Games & OUR CALL', dest: 'games' },
  go_board:      { g: 'Main', icon: '👥', title: 'Players (full board)', dest: 'players' },
  go_record:     { g: 'Main', icon: '📑', title: 'Record', dest: 'record' },
  go_locks:      { g: 'This Week', icon: '🔒', title: 'Most likely to cash', dest: 'home:locks' },
  go_fades:      { g: 'This Week', icon: '⚠️', title: 'Unders (overs to fade)', dest: 'home:fades' },
  go_paydirt:    { g: 'Touchdowns', icon: '🏈', title: 'Pay Dirt', dest: 'tdlab' },
  go_scorers:    { g: 'Touchdowns', icon: '📋', title: 'Most likely to score', dest: 'scorers' },
  go_valuetds:   { g: 'Touchdowns', icon: '💎', title: 'Value touchdown plays', dest: 'valuetds' },
  go_tdroles:    { g: 'Touchdowns', icon: '🎯', title: 'TD Matchups', dest: 'tdroles' },
  go_due:        { g: 'Touchdowns', icon: '🔥', title: 'Due', dest: 'due' },
  go_defense:    { g: 'Matchups', icon: '🛡️', title: 'Defense breakdown', dest: 'defense' },
  go_sheets:     { g: 'Matchups', icon: '🔥', title: 'Matchup Sheets', dest: 'sheets' },
  go_blowout:    { g: 'Matchups', icon: '💥', title: 'Blowout Spots', dest: 'blowout' },
  go_ladder:     { g: 'Matchups', icon: '🪜', title: 'Ladder Spotter', dest: 'more:ladder' },
  ...Object.fromEntries(NFL_MARKETS.map(mk => [`go_mkt_${mk.key}`,
    { g: 'Rankings', icon: '📊', title: `${mk.label} rankings`, dest: `mkt:${mk.key}` }])),
  go_pulse:      { g: 'Markets', icon: '📊', title: 'Market Pulse', dest: 'more:pulse' },
  go_gamelines:  { g: 'Markets', icon: '💰', title: 'Game Lines', dest: 'more:gamelines' },
  go_pickem:     { g: 'Markets', icon: '🎯', title: 'Pick’em +EV', dest: 'more:pickem' },
  go_futures:    { g: 'Markets', icon: '🏆', title: 'Futures', dest: 'more:futures' },
  go_fantasy:    { g: 'More tools', icon: '🧮', title: 'Fantasy', dest: 'more:fantasy' },
  go_trends:     { g: 'More tools', icon: '📈', title: 'Trends', dest: 'more:trends' },
  go_sim:        { g: 'More tools', icon: '🎲', title: 'Sim Check (Pro)', dest: 'more:sim' },
  go_community:  { g: 'More tools', icon: '🏟️', title: 'Community', dest: 'community' },
  go_refer:      { g: 'Account', icon: '🎁', title: 'Refer & Earn', dest: 'refer' },
  go_guide:      { g: 'Account', icon: '📖', title: 'How to read LINEBRKR', dest: 'glossary' },
}
const GROUPS = ['Main', 'This Week', 'Touchdowns', 'Matchups', 'Rankings', 'Markets', 'More tools', 'Account']
const META = { ...SECTIONS, ...SHORTCUTS }
const ALL = [...Object.keys(SECTIONS), ...Object.keys(SHORTCUTS)]
const TEMPLATES = {
  Everything:    ['record', 'locks', 'scorers', 'tdroles', 'fades', 'slate', 'pulse', 'pickem', 'refer'],
  'TD hunter':   ['scorers', 'tdroles', 'go_paydirt', 'go_due', 'go_mkt_anytime_td'],
  Sharp:         ['pulse', 'record', 'fades', 'pickem', 'go_defense', 'go_gamelines'],
  'Quick links': ['go_studio', 'go_tds', 'go_paydirt', 'go_defense', 'go_sheets', 'go_board', 'go_games', 'go_record'],
  Simple:        ['record', 'locks', 'scorers'],
}
function load() {
  try { const v = JSON.parse(localStorage.getItem(KEY)); if (Array.isArray(v) && v.length) return v.filter(x => ALL.includes(x)) } catch (_) {}
  return TEMPLATES.Everything
}

const card = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 14, padding: '14px 15px', marginBottom: 12 }
const head = { fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: T.green, marginBottom: 10 }
function useOne(fn, deps) { const [d, setD] = useState(null); useEffect(() => { let a = true; setD(null); fn().then(r => { if (a) setD(r) }).catch(() => { if (a) setD({}) }); return () => { a = false } }, deps); return d }

// Live-card shell — the "Open ›" link jumps to the full screen behind the card.
const NavCtx = React.createContext(null)
function Section({ id, children }) {
  const go = React.useContext(NavCtx)
  const dest = SECTIONS[id]?.dest
  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ ...head, marginBottom: 0 }}>{META[id].icon} {META[id].title}</div>
        {dest && go && <button onClick={() => go(dest)} style={{ background: 'none', border: 'none', cursor: 'pointer',
          color: T.green, fontSize: 12, fontWeight: 800, padding: 0 }}>Open ›</button>}
      </div>
      {children}
    </div>
  )
}
// One compact row for the list cards (name · detail · number).
function MiniRow({ name, sub, val, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, fontSize: 12.5, padding: '3px 0' }}>
      <span style={{ color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{name} <span style={{ color: T.muted }}>{sub}</span></span>
      <span style={{ fontFamily: T.mono, fontWeight: 800, color, flexShrink: 0 }}>{val}</span>
    </div>
  )
}
const empty = msg => <div style={{ fontSize: 12, color: T.muted }}>{msg}</div>

function LocksCard({ season, week }) {
  const d = useOne(() => getNFLLocks(season, week), [season, week])
  if (!d) return <Section id="locks"><Spinner /></Section>
  const rows = (d.locks || []).slice(0, 5)
  return (
    <Section id="locks">
      {rows.length ? rows.map((r, i) => <MiniRow key={i} name={r.name}
        sub={r.prop_type === 'anytime_td' ? 'Anytime TD' : `${playText(r.market, r.line)}`}
        val={`${Math.round((r.prob || 0) * 100)}%`} color={playColor(r.prob || 0, r)} />)
        : empty('Fills as the board and lines post.')}
    </Section>
  )
}
function FadesCard({ season, week }) {
  const d = useOne(() => getNFLFades(season, week), [season, week])
  if (!d) return <Section id="fades"><Spinner /></Section>
  const rows = (d.fades || []).slice(0, 5)
  return (
    <Section id="fades">
      {rows.length ? rows.map((r, i) => <MiniRow key={i} name={r.name} sub={playText(r.market, r.line, 'under')}
        val={`${Math.round((r.prob || 0) * 100)}%`} color={T.amber} />)
        : empty('Fills once book lines post.')}
    </Section>
  )
}
function ScorersCard({ season, week }) {
  const d = useOne(() => getNFLScorers(season, week, 10), [season, week])
  if (!d) return <Section id="scorers"><Spinner /></Section>
  const rows = (d.games || []).flatMap(g => [...(g.away_scorers || []), ...(g.home_scorers || [])])
    .sort((a, b) => (b.prob || 0) - (a.prob || 0)).slice(0, 5)
  return (
    <Section id="scorers">
      {rows.length ? rows.map((r, i) => <MiniRow key={i} name={r.name} sub={`${teamNick(r.team)} vs ${teamNick(r.opp)}`}
        val={`${Math.round((r.prob || 0) * 100)}%`} color={tdColor(r.prob || 0)} />)
        : empty('No scorers yet this week.')}
    </Section>
  )
}
function TdRolesCard({ season, week }) {
  const d = useOne(() => getNFLTdRoles(season, week), [season, week])
  if (!d) return <Section id="tdroles"><Spinner /></Section>
  const rows = (d.players || []).filter(p => tdGrade(p.opp_rank, p.opp_mult).w === 'Great')
    .sort((a, b) => (b.model_td_prob ?? -1) - (a.model_td_prob ?? -1)).slice(0, 5)
  return (
    <Section id="tdroles">
      {rows.length ? rows.map((p, i) => <MiniRow key={i} name={p.name} sub={`${roleText(p.role, p.role_label)} vs ${teamNick(p.opp)}`}
        val={p.model_td_prob != null ? `${Math.round(p.model_td_prob * 100)}%` : 'Great'} color={tdColor(p.model_td_prob || 0)} />)
        : empty('No standout TD matchups yet this week.')}
      <div style={{ fontSize: 11, color: T.faint, marginTop: 6 }}>Facing a defense that gives up the most TDs to his role.</div>
    </Section>
  )
}

function RecordCard({ season }) {
  const d = useOne(() => getNFLScorecard(season, 0), [season])
  if (!d) return <Section id="record"><Spinner /></Section>
  const o = d.overall || {}, r = d.realized || {}
  return (
    <Section id="record">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
        <span style={{ fontSize: 34, fontWeight: 900, color: T.green, fontFamily: T.mono, lineHeight: .9 }}>{o.hit_rate != null ? `${Math.round(o.hit_rate)}%` : '—'}</span>
        <span style={{ fontSize: 14, color: T.muted, fontFamily: T.mono }}>{o.wins || 0}–{o.losses || 0}</span>
        {r.clv_positive_rate != null && <span style={{ fontSize: 12, color: T.muted }}>beat close <b style={{ color: T.green }}>{r.clv_positive_rate}%</b></span>}
      </div>
      <div style={{ fontSize: 11, color: T.faint, marginTop: 6 }}>Recommended plays · CLV-proven · see Record for the full chart.</div>
    </Section>
  )
}
function SlateCard({ season, week }) {
  const d = useOne(() => getNFLSlate(season, week), [season, week])
  if (!d) return <Section id="slate"><Spinner /></Section>
  const games = d.games || []
  const td = games.filter(g => g.proj_tds != null)
  const total = td.reduce((s, g) => s + (g.proj_tds || 0), 0)
  return (
    <Section id="slate">
      <div style={{ fontSize: 13, color: T.text }}><b>{games.length}</b> games this week{total ? <> · <b style={{ color: T.green }}>~{Math.round(total)}</b> TDs projected</> : ''}</div>
      <div style={{ fontSize: 11, color: T.faint, marginTop: 6 }}>Open Games for OUR CALL on each matchup.</div>
    </Section>
  )
}
function PulseCard({ season, week }) {
  const d = useOne(() => getNFLMarketPulse(season, week), [season, week])
  if (!d) return <Section id="pulse"><Spinner /></Section>
  const movers = (d.movers || []).slice(0, 3)
  if (!movers.length) return <Section id="pulse"><div style={{ fontSize: 12, color: T.muted }}>No line movement yet.</div></Section>
  return (
    <Section id="pulse">
      {movers.map((r, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '3px 0' }}>
          <span style={{ color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name} <span style={{ color: T.muted }}>{r.market}</span>{r.agree === true ? <span style={{ color: T.green }}> ✓</span> : null}</span>
          <span style={{ fontFamily: T.mono, fontWeight: 800, color: (r.move || 0) > 0 ? T.green : T.amber }}>{(r.move || 0) > 0 ? '+' : ''}{r.move}</span>
        </div>
      ))}
    </Section>
  )
}
function PickemCard({ season, week }) {
  const d = useOne(() => getNFLPickemBoard(season, week), [season, week])
  if (!d) return <Section id="pickem"><Spinner /></Section>
  const plays = (d.plays || []).slice(0, 3)
  if (!plays.length) return <Section id="pickem"><div style={{ fontSize: 12, color: T.muted }}>{d.dfs_enabled ? 'No pick’em lines yet.' : 'Pick’em board is off (needs the DFS data plan).'}</div></Section>
  return (
    <Section id="pickem">
      {plays.map((p, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '3px 0' }}>
          <span style={{ color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name} <span style={{ color: T.muted }}>{p.app} {p.side === 'over' ? 'O' : 'U'}{p.dfs_line}</span></span>
          <span style={{ fontFamily: T.mono, fontWeight: 800, color: probColor((p.prob || 0)) }}>{Math.round((p.prob || 0) * 100)}%</span>
        </div>
      ))}
    </Section>
  )
}
function ReferCard() {
  const d = useOne(() => getNFLReferral(), [])
  if (!d) return <Section id="refer"><Spinner /></Section>
  if (!d.ref_code) return <Section id="refer"><div style={{ fontSize: 12, color: T.muted }}>Your referral link appears once your account is set up.</div></Section>
  return (
    <Section id="refer">
      <div style={{ display: 'flex', gap: 12 }}>
        <div><div style={{ fontSize: 20, fontWeight: 900, color: T.green }}>${d.earned || 0}</div><div style={{ fontSize: 10, color: T.muted }}>EARNED</div></div>
        <div><div style={{ fontSize: 20, fontWeight: 900, color: T.text }}>{d.qualified || 0}</div><div style={{ fontSize: 10, color: T.muted }}>QUALIFIED</div></div>
        <div style={{ flex: 1 }} />
      </div>
      <div style={{ fontSize: 11, color: T.faint, marginTop: 6 }}>Code <b style={{ color: T.text }}>{d.ref_code.toUpperCase()}</b> · open 🎁 Refer to share.</div>
    </Section>
  )
}

const RENDER = { record: RecordCard, slate: SlateCard, locks: LocksCard, fades: FadesCard, scorers: ScorersCard,
  tdroles: TdRolesCard, pulse: PulseCard, pickem: PickemCard, refer: ReferCard }

// Shortcut tile — a one-tap jump to any screen.
function LaunchTile({ id, go }) {
  const s = SHORTCUTS[id]
  return (
    <button onClick={() => go(s.dest)} style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 12, ...card, cursor: 'pointer', textAlign: 'left' }}>
      <span style={{ width: 34, height: 34, borderRadius: 9, background: T.card2, display: 'grid', placeItems: 'center', fontSize: 17, flexShrink: 0 }}>{s.icon}</span>
      <span style={{ flex: 1, fontSize: 14.5, fontWeight: 800, color: T.text }}>{s.title}</span>
      <span style={{ color: T.green, fontSize: 18 }}>→</span>
    </button>
  )
}

const label = { fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', color: T.muted, textTransform: 'uppercase', margin: '14px 0 8px' }

export default function MyBoardTab({ season = 2026, week = 1, onNavigate, onRefer }) {
  const [order, setOrder] = useState(load)
  const [edit, setEdit] = useState(false)
  useEffect(() => { try { localStorage.setItem(KEY, JSON.stringify(order)) } catch (_) {} }, [order])
  const move = (id, dir) => { const i = order.indexOf(id), j = i + dir; if (j < 0 || j >= order.length) return
    const a = [...order]; [a[i], a[j]] = [a[j], a[i]]; setOrder(a) }
  const toggle = (id) => { track(order.includes(id) ? 'myboard_remove' : 'myboard_add', id); setOrder(order.includes(id) ? order.filter(x => x !== id) : [...order, id]) }
  const go = (dest) => { track('myboard_open', dest); if (dest === 'refer') { onRefer && onRefer(); return } onNavigate && onNavigate(dest) }
  const btn = (on) => ({ padding: '6px 11px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 800,
    border: `1px solid ${on ? T.green : T.border}`, background: on ? T.greenDim : T.card, color: on ? T.green : T.muted })
  const addRow = id => (
    <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: T.card2, border: `1px solid ${T.border}`, borderRadius: 11, padding: '10px 13px', marginBottom: 7 }}>
      <span style={{ fontSize: 16 }}>{META[id].icon}</span>
      <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700, color: T.muted2 }}>{META[id].title}</span>
      <button onClick={() => toggle(id)} style={{ ...btn(true), padding: '5px 12px' }}>+ Add</button>
    </div>
  )
  const availCards = Object.keys(SECTIONS).filter(id => !order.includes(id))
  return (
    <NavCtx.Provider value={go}>
    <div style={{ padding: '12px 16px 80px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: T.muted, lineHeight: 1.4 }}>Your board — add anything in the app, in your order. Saved on this device.</div>
        <button onClick={() => setEdit(e => !e)} style={{ ...btn(edit), flexShrink: 0, marginLeft: 10 }}>{edit ? 'Done' : 'Edit'}</button>
      </div>

      {edit ? (
        <div>
          <div style={{ ...label, marginTop: 0 }}>Start from a template</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.keys(TEMPLATES).map(t => (
              <button key={t} onClick={() => { setOrder([...TEMPLATES[t]]) }} style={btn(false)}>{t}</button>
            ))}
          </div>
          <div style={label}>On your board{order.length ? ` · ${order.length}` : ''}</div>
          {!order.length && <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>Nothing yet — add from the lists below.</div>}
          {order.map((id, i) => (
            <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, background: T.card, border: `1px solid ${T.greenMid}`, borderRadius: 11, padding: '10px 13px', marginBottom: 7 }}>
              <span style={{ fontSize: 16 }}>{META[id].icon}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700 }}>{META[id].title}
                <span style={{ display: 'block', fontSize: 10, color: T.faint, fontWeight: 700 }}>{SECTIONS[id] ? 'Live card' : 'Shortcut'}</span></span>
              <button onClick={() => move(id, -1)} disabled={i === 0} aria-label="Move up" style={{ ...btn(false), opacity: i === 0 ? 0.4 : 1, padding: '5px 10px' }}>↑</button>
              <button onClick={() => move(id, 1)} disabled={i === order.length - 1} aria-label="Move down" style={{ ...btn(false), opacity: i === order.length - 1 ? 0.4 : 1, padding: '5px 10px' }}>↓</button>
              <button onClick={() => toggle(id)} aria-label="Remove" style={{ ...btn(false), padding: '5px 10px', color: T.amber }}>✕</button>
            </div>
          ))}
          {availCards.length > 0 && <>
            <div style={label}>Live cards <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 600, color: T.faint }}>· a summary right on your board</span></div>
            {availCards.map(addRow)}
          </>}
          {GROUPS.map(g => {
            const ids = Object.keys(SHORTCUTS).filter(id => SHORTCUTS[id].g === g && !order.includes(id))
            if (!ids.length) return null
            return <React.Fragment key={g}>
              <div style={label}>{g} <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 600, color: T.faint }}>· shortcuts</span></div>
              {ids.map(addRow)}
            </React.Fragment>
          })}
        </div>
      ) : (
        order.length === 0
          ? <div style={{ color: T.muted, fontSize: 13, textAlign: 'center', padding: '30px 0' }}>Your board is empty — tap <b style={{ color: T.green }}>Edit</b> to add anything from the app.</div>
          : order.map(id => SHORTCUTS[id]
              ? <LaunchTile key={id} id={id} go={go} />
              : (() => { const C = RENDER[id]; return <C key={id} season={season} week={week} /> })())
      )}
    </div>
    </NavCtx.Provider>
  )
}
